import type { BooleanInput } from '@angular/cdk/coercion';
import type { OnDestroy, OnInit } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Input, ViewEncapsulation } from '@angular/core';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { Router } from '@angular/router';
import { AuthService } from 'app/core/auth/auth.service';
import { UserService } from 'app/core/user/user.service';
import type { User } from 'app/core/user/user.types';
import { Subject } from 'rxjs';

@Component({
  selector: 'spri-user',
  templateUrl: './user.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  exportAs: 'user',
  imports: [MatMenuModule, MatIconModule, MatDividerModule],
})
export class UserComponent implements OnInit, OnDestroy {
  static ngAcceptInputType_showAvatar: BooleanInput;

  @Input() showAvatar = true;
  user: User;

  private _unsubscribeAll = new Subject<void>();

  /**
   * Constructor
   */
  private _changeDetectorRef = inject(ChangeDetectorRef);
  private _router = inject(Router);
  private _userService = inject(UserService);
  private _authService = inject(AuthService);

  usernameAbbreviation: string | null = null;

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    this.getLogInUserDetail();
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Update the user status
   *
   * @param status
   */
  updateUserStatus(status: string): void {
    // Return if user is not available
    if (!this.user) {
      return;
    }

    // Update the user
    this._userService
      .update({
        ...this.user,
        status,
      })
      .subscribe();
  }

  /**
   * Sign out
   */
  signOut(): void {
    this._authService.signOut(false).subscribe((result) => {
      if (result) {
        this._router.navigate(['/sign-in'], { replaceUrl: true });
      }
    });
  }

  /**
   * Navigate to profile
   */
  profile(): void {
    this._router.navigate(['/profile']);
  }

  public getLogInUserDetail() {
    const userdetail = this._authService.getSignInUserDetail();
    this.usernameAbbreviation = this.getUsernameAbbreviation(userdetail?.name || '');
  }

  // get username abbreviation
  getUsernameAbbreviation(name: string): string {
    const nameParts = name.split(' ');
    if (nameParts.length > 1) {
      return nameParts[0].charAt(0) + nameParts[1].charAt(0);
    }
    return name.charAt(0);
  }
}
